import { useState, useEffect } from 'react';
import { supabase, safeRefreshSession } from './supabase';
import { supabaseData } from './supabaseData';
import { Users, Mail, UserPlus, Trash2, Shield, Crown, Settings as SettingsIcon, X, Zap, Plus, Minus, MessageSquare, Phone, Clock, Calendar, UserCheck, UserX, RefreshCw, Lock, Check } from 'lucide-react';
import { useModal } from './AppModal';
import { useFeatures } from './FeatureContext';
import { useTheme } from './ThemeContext';

export default function TeamManagement({ companyId, userRole, onNavigate }) {
  const { showAlert, showConfirm, showSuccess, showError } = useModal();
  const { canAddTeamMember, getLimit, tierName, teamCount, hasFeature, canPurchaseMoreSeats, getRemainingPurchasableSeats, seatPrice, tier, refresh: refreshFeatures, hasFlexWorkers, getFlexDaysIncluded } = useFeatures();
  const { colors } = useTheme();
  const [loading, setLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [teamStats, setTeamStats] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Invite form state
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitePhone, setInvitePhone] = useState('');
  const [inviteMethod, setInviteMethod] = useState('email'); // 'email', 'sms', 'both'
  const [inviteRole, setInviteRole] = useState('field_tech'); // Default to Field Tech for simplicity
  const [sending, setSending] = useState(false);

  // Seat purchase state
  const [seatsToBuy, setSeatsToBuy] = useState(1);
  const [buyingSeats, setBuyingSeats] = useState(false);
  const SEAT_PRICE = seatPrice || 5.00;

  // Resend invite state
  const [showResendModal, setShowResendModal] = useState(false);
  const [resendInvite, setResendInvite] = useState(null);
  const [resendEmail, setResendEmail] = useState('');
  const [resendPhone, setResendPhone] = useState('');
  const [resendMethod, setResendMethod] = useState('email');
  const [resending, setResending] = useState(false);

  // Flex worker state
  const [flexWorkers, setFlexWorkers] = useState([]);
  const [flexDaysUsed, setFlexDaysUsed] = useState(0);
  const [showFlexModal, setShowFlexModal] = useState(false);
  const [flexForm, setFlexForm] = useState({ name: '', phone: '', email: '' });
  const [savingFlex, setSavingFlex] = useState(false);

  // Role permissions state
  const [showPermissions, setShowPermissions] = useState(false);
  const [rolePermissions, setRolePermissions] = useState(null);
  const [savingPermissions, setSavingPermissions] = useState(false);

  const isOwnerOrAdmin = userRole === 'owner' || userRole === 'admin';
  const flexDaysIncluded = getFlexDaysIncluded();
  const flexDaysRemaining = Math.max(0, flexDaysIncluded - flexDaysUsed);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Track mount state to avoid state updates after unmount
  const [loadError, setLoadError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (companyId) {
      // Reset error state on mount/companyId change
      setLoadError(null);
      loadTeamData();
    } else {
      setLoading(false);
    }
  }, [companyId, retryCount]);

  const loadTeamData = async () => {
    try {
      setLoading(true);
      setLoadError(null);

      // Use allSettled so one failure doesn't break everything
      const results = await Promise.allSettled([
        loadTeamMembers(),
        loadPendingInvites(),
        loadTeamStats(),
        loadFlexWorkers()
      ]);

      // Check for any failures
      const failures = results.filter(r => r.status === 'rejected');
      if (failures.length > 0) {
        console.warn('TeamManagement: Some queries failed:', failures.map(f => f.reason?.message || f.reason));
      }
    } catch (error) {
      console.error('TeamManagement: Failed to load team data:', error);
      setLoadError(error.message || 'Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
  };

  const loadTeamMembers = async () => {
    try {
      const { data: teamData, error } = await supabaseData
        .from('user_companies')
        .select('*')
        .eq('company_id', companyId);

      if (error) {
        console.error('TeamManagement: failed to load team:', error.message);
        return;
      }

      // Get user profiles separately
      const userIds = (teamData || []).map(m => m.user_id);
      let profilesMap = {};
      if (userIds.length > 0) {
        const { data: profiles } = await supabaseData
          .from('user_profiles')
          .select('id, full_name, email, role')
          .in('id', userIds);
        (profiles || []).forEach(p => { profilesMap[p.id] = p; });
      }

      const transformed = (teamData || []).map(row => ({
        id: row.id,
        user_id: row.user_id,
        company_id: row.company_id,
        role: row.role,
        created_at: row.created_at,
        user_profiles: profilesMap[row.user_id] || null
      }));
      setTeamMembers(transformed);
    } catch (err) {
      console.error('TeamManagement: loadTeamMembers error:', err);
    }
  };

  const loadPendingInvites = async () => {
    const { data, error } = await supabaseData
      .from('team_invitations')
      .select('*')
      .eq('company_id', companyId)
      .eq('status', 'pending')
      .order('created_at', { ascending: false });

    if (error) {
    } else {
      setPendingInvites(data || []);
    }
  };

  const loadTeamStats = async () => {
    // Stats computed from teamMembers and pendingInvites directly — no separate query needed
  };

  const loadFlexWorkers = async () => {
    if (!hasFlexWorkers()) return;

    try {
      // Load flex workers
      const { data: workers, error: workersError } = await supabaseData
        .from('flex_workers')
        .select('*')
        .eq('company_id', companyId)
        .order('name');

      if (!workersError) {
        setFlexWorkers(workers || []);
      }

      // Calculate flex days used this month
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];

      const { count, error: countError } = await supabaseData
        .from('flex_activations')
        .select('*', { count: 'exact', head: true })
        .eq('company_id', companyId)
        .gte('activation_date', startOfMonth);

      if (!countError) {
        setFlexDaysUsed(count || 0);
      }
    } catch (error) {
      // Tables might not exist yet - that's OK
    }
  };

  const addFlexWorker = async () => {
    const trimmedName = flexForm.name.trim();
    const trimmedPhone = flexForm.phone.trim();
    const trimmedEmail = flexForm.email.trim();

    if (!trimmedName) {
      showError('Please enter a name');
      return;
    }

    // Duplicate prevention — match on name + phone OR name + email (case-insensitive)
    const normalizedPhone = trimmedPhone.replace(/\D/g, '');
    const dup = flexWorkers.find(w => {
      const sameName = w.name?.trim().toLowerCase() === trimmedName.toLowerCase();
      if (!sameName) return false;
      const wPhone = (w.phone || '').replace(/\D/g, '');
      const wEmail = (w.email || '').trim().toLowerCase();
      // Treat as duplicate if name matches AND (phone matches OR email matches OR both are blank)
      if (normalizedPhone && wPhone && normalizedPhone === wPhone) return true;
      if (trimmedEmail && wEmail && trimmedEmail.toLowerCase() === wEmail) return true;
      if (!trimmedPhone && !trimmedEmail && !w.phone && !w.email) return true;
      return false;
    });
    if (dup) {
      showError(`A flex worker named "${trimmedName}" with those details already exists`);
      return;
    }

    setSavingFlex(true);
    try {
      const { error } = await supabaseData
        .from('flex_workers')
        .insert({
          company_id: companyId,
          name: trimmedName,
          phone: trimmedPhone || null,
          email: trimmedEmail || null
        });

      if (error) throw error;

      showSuccess('Flex worker added!');
      setFlexForm({ name: '', phone: '', email: '' });
      setShowFlexModal(false);
      loadFlexWorkers();
    } catch (error) {
      showError('Failed to add flex worker: ' + error.message);
    } finally {
      setSavingFlex(false);
    }
  };

  const activateFlexWorker = async (worker) => {
    if (flexDaysRemaining <= 0) {
      showError('No flex days remaining this month. Upgrade your plan or wait until next month.');
      return;
    }

    const confirmed = await showConfirm(
      `Activate ${worker.name} for today? This will use 1 flex day (${flexDaysRemaining - 1} remaining).`
    );
    if (!confirmed) return;

    try {
      const today = new Date().toISOString().split('T')[0];

      // Check if already activated today
      const { data: existing } = await supabaseData
        .from('flex_activations')
        .select('id')
        .eq('flex_worker_id', worker.id)
        .eq('activation_date', today)
        .single();

      if (existing) {
        showError(`${worker.name} is already activated for today.`);
        return;
      }

      const { error } = await supabaseData
        .from('flex_activations')
        .insert({
          company_id: companyId,
          flex_worker_id: worker.id,
          activation_date: today
        });

      if (error) throw error;

      showSuccess(`${worker.name} activated for today!`);
      setFlexDaysUsed(prev => prev + 1);
    } catch (error) {
      showError('Failed to activate: ' + error.message);
    }
  };

  const removeFlexWorker = async (worker) => {
    const confirmed = await showConfirm(`Remove ${worker.name} from your flex worker pool?`);
    if (!confirmed) return;

    try {
      const { error } = await supabaseData
        .from('flex_workers')
        .delete()
        .eq('id', worker.id);

      if (error) throw error;

      showSuccess('Flex worker removed');
      loadFlexWorkers();
    } catch (error) {
      showError('Failed to remove: ' + error.message);
    }
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    // Remove all non-digits
    const digits = String(phone).replace(/\D/g, '');
    // Format as (XXX) XXX-XXXX
    if (digits.length >= 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
    return phone;
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();

    const needsEmail = inviteMethod === 'email' || inviteMethod === 'both';
    const needsPhone = inviteMethod === 'sms' || inviteMethod === 'both';

    if (needsEmail && !inviteEmail) {
      showAlert('Please enter an email address', { title: 'Missing Info' });
      return;
    }
    if (needsPhone && !invitePhone) {
      showAlert('Please enter a phone number', { title: 'Missing Info' });
      return;
    }
    if (!inviteRole) {
      showAlert('Please select a role', { title: 'Missing Info' });
      return;
    }

    // Validate email if needed
    if (needsEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(inviteEmail)) {
        showAlert('Please enter a valid email address', { title: 'Invalid Email' });
        return;
      }
    }

    // Validate phone if needed
    if (needsPhone) {
      const phoneDigits = invitePhone.replace(/\D/g, '');
      if (phoneDigits.length < 10) {
        showAlert('Please enter a valid 10-digit phone number', { title: 'Invalid Phone' });
        return;
      }
    }

    // Check seat limit using feature context
    const maxUsers = getLimit('maxUsers');
    const totalUsersWithPending = teamMembers.length + pendingInvites.length;
    if (totalUsersWithPending >= maxUsers) {
      showError(`You've reached your ${tierName} plan limit of ${maxUsers} team member${maxUsers === 1 ? '' : 's'}. Upgrade to add more!`);
      return;
    }

    setSending(true);
    try {
      // Check if there's already a pending invitation with this email or phone
      const lookupEmail = needsEmail ? inviteEmail.toLowerCase() : null;
      const lookupPhone = needsPhone ? invitePhone.replace(/\D/g, '') : null;

      let existingQuery = supabaseData
        .from('team_invitations')
        .select('*')
        .eq('company_id', companyId)
        .eq('status', 'pending');

      if (lookupEmail) {
        existingQuery = existingQuery.eq('email', lookupEmail);
      }

      const { data: existingInvite } = await existingQuery.maybeSingle();

      if (existingInvite) {
        showAlert('An invitation has already been sent to this contact!', { title: 'Already Invited' });
        setSending(false);
        return;
      }

      // Check if email is already registered with an existing account
      if (lookupEmail) {
        const { data: emailCheck, error: emailCheckError } = await supabaseData
          .rpc('check_email_available', { p_email: lookupEmail });

        if (!emailCheckError && emailCheck && !emailCheck.available) {
          showAlert(emailCheck.reason || 'This email is already registered with an existing account.', { title: 'Email Already In Use' });
          setSending(false);
          return;
        }
      }

      // Get current user ID and company name
      const { data: { user } } = await supabase.auth.getUser();
      const { data: companyData } = await supabaseData
        .from('companies')
        .select('name')
        .eq('id', companyId)
        .single();

      const companyName = companyData?.name || 'Your Team';

      // Create invitation record
      const { data: invite, error } = await supabaseData
        .from('team_invitations')
        .insert([{
          company_id: companyId,
          invited_by: user.id,
          email: lookupEmail,
          phone: lookupPhone,
          invite_method: inviteMethod,
          role: inviteRole
        }])
        .select()
        .single();

      if (error) throw error;

      const appUrl = process.env.REACT_APP_APP_URL || 'https://workvanapp.com';
      const inviteLink = `${appUrl}/?invite=${invite.id}`;

      // Send email if needed
      if (needsEmail && lookupEmail) {
        try {
          await fetch('/api/email-send', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              to: lookupEmail,
              subject: `You're invited to join ${companyName} on Work Van`,
              html: `
                <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                  <h2 style="color: #1a1a1a;">You've been invited!</h2>
                  <p style="color: #666;">Hi there,</p>
                  <p style="color: #666;"><strong>${companyName}</strong> has invited you to join their team on Work Van as a <strong>${inviteRole.replace('_', ' ')}</strong>.</p>
                  <div style="text-align: center; margin: 2rem 0;">
                    <a href="${inviteLink}" style="background: #F05A28; color: white; padding: 1rem 2rem; text-decoration: none; border-radius: 8px; font-weight: bold; display: inline-block;">
                      Accept Invitation
                    </a>
                  </div>
                  <p style="color: #999; font-size: 0.9rem;">Or copy this link: ${inviteLink}</p>
                </div>
              `,
              companyId: companyId
            })
          });
        } catch (emailError) {
        }
      }

      // Send SMS if needed
      if (needsPhone && lookupPhone) {
        try {
          await fetch('/api/sms-send', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              to: lookupPhone,
              message: `You've been invited to join ${companyName} on Work Van! Sign up here: ${inviteLink}`
            })
          });
        } catch (smsError) {
        }
      }

      const sentTo = needsEmail && needsPhone
        ? `${inviteEmail} and ${formatPhoneNumber(invitePhone)}`
        : needsEmail ? inviteEmail : formatPhoneNumber(invitePhone);

      showSuccess(`Invitation sent to ${sentTo}! They'll be added to your team when they sign up.`);

      setShowInviteModal(false);
      setInviteEmail('');
      setInvitePhone('');
      setInviteMethod('email');
      setInviteRole('field_tech');
      loadTeamData(); // Refresh data
    } catch (error) {
      showError('Failed to send invitation: ' + error.message);
    } finally {
      setSending(false);
    }
  };

  const openResendModal = (invite) => {
    setResendInvite(invite);
    setResendEmail(invite.email || '');
    setResendPhone(invite.phone ? formatPhoneNumber(invite.phone) : '');
    setResendMethod(invite.invite_method || 'email');
    setShowResendModal(true);
  };

  const handleResendInvite = async () => {
    if (!resendInvite) return;

    const needsEmail = resendMethod === 'email' || resendMethod === 'both';
    const needsPhone = resendMethod === 'sms' || resendMethod === 'both';
    const emailVal = resendEmail.trim().toLowerCase();
    const phoneVal = resendPhone.replace(/\D/g, '');

    if (needsEmail && !emailVal) {
      showError('Please enter an email address');
      return;
    }
    if (needsPhone && phoneVal.length < 10) {
      showError('Please enter a valid phone number');
      return;
    }

    setResending(true);
    try {
      // Update invite record with new contact info
      const updateData = {
        email: needsEmail ? emailVal : resendInvite.email,
        phone: needsPhone ? phoneVal : resendInvite.phone,
        invite_method: resendMethod
      };

      const { error: updateError } = await supabaseData
        .from('team_invitations')
        .update(updateData)
        .eq('id', resendInvite.id);

      if (updateError) throw updateError;

      // Get company name
      const { data: companyData } = await supabaseData
        .from('companies')
        .select('name')
        .eq('id', companyId)
        .single();

      const companyName = companyData?.name || 'Your Team';
      const appUrl = process.env.REACT_APP_APP_URL || 'https://workvanapp.com';
      const inviteLink = `${appUrl}/?invite=${resendInvite.id}`;

      // Send email
      if (needsEmail && emailVal) {
        try {
          await fetch('/api/email-send', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              to: emailVal,
              subject: `You're invited to join ${companyName} on Work Van`,
              html: `
                <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                  <h2 style="color: #1a1a1a;">You've been invited!</h2>
                  <p style="color: #666;">Hi there,</p>
                  <p style="color: #666;"><strong>${companyName}</strong> has invited you to join their team on Work Van as a <strong>${(resendInvite.role || 'member').replace('_', ' ')}</strong>.</p>
                  <div style="text-align: center; margin: 2rem 0;">
                    <a href="${inviteLink}" style="background: #F05A28; color: white; padding: 1rem 2rem; text-decoration: none; border-radius: 8px; font-weight: bold; display: inline-block;">
                      Accept Invitation
                    </a>
                  </div>
                  <p style="color: #999; font-size: 0.9rem;">Or copy this link: ${inviteLink}</p>
                </div>
              `,
              companyId: companyId
            })
          });
        } catch (emailError) {
        }
      }

      // Send SMS
      if (needsPhone && phoneVal) {
        try {
          await fetch('/api/sms-send', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              to: phoneVal,
              message: `You've been invited to join ${companyName} on Work Van! Sign up here: ${inviteLink}`
            })
          });
        } catch (smsError) {
        }
      }

      showSuccess('Invitation resent!');
      setShowResendModal(false);
      setResendInvite(null);
      loadTeamData();
    } catch (error) {
      showError('Failed to resend: ' + error.message);
    } finally {
      setResending(false);
    }
  };

  const handleCancelInvite = async (inviteId) => {
    const confirmed = await showConfirm('Cancel this invitation?', { title: 'Cancel Invite', confirmText: 'Yes, Cancel', confirmStyle: 'danger' });
    if (!confirmed) return;

    try {
      const { error } = await supabaseData
        .from('team_invitations')
        .delete()
        .eq('id', inviteId);

      if (error) throw error;

      loadTeamData();
    } catch (error) {
      showError('Failed to cancel invitation: ' + error.message);
    }
  };

  const handleRemoveMember = async (memberId, memberEmail) => {
    const confirmed = await showConfirm(`Remove ${memberEmail} from the team?`, { title: 'Remove Member', confirmText: 'Remove', confirmStyle: 'danger' });
    if (!confirmed) return;

    try {
      const { error } = await supabaseData
        .from('user_companies')
        .delete()
        .eq('id', memberId);

      if (error) throw error;

      showSuccess('Team member removed');
      loadTeamData();
    } catch (error) {
      showError('Failed to remove member: ' + error.message);
    }
  };

  const handleChangeRole = async (memberId, newRole) => {
    try {
      const { error } = await supabaseData
        .from('user_companies')
        .update({ role: newRole })
        .eq('id', memberId);

      if (error) throw error;
      loadTeamData();
    } catch (error) {
      showError('Failed to update role: ' + error.message);
    }
  };

  // Handle buying additional seats
  const handleBuySeats = async () => {
    if (seatsToBuy < 1) return;

    const totalCost = seatsToBuy * SEAT_PRICE;
    const confirmed = await showConfirm(
      `Add ${seatsToBuy} team seat${seatsToBuy > 1 ? 's' : ''} for $${totalCost.toFixed(2)}/month?\n\nThis will be added to your next billing cycle.`,
      { title: 'Add Team Seats', confirmText: 'Add Seats', confirmStyle: 'success' }
    );

    if (!confirmed) return;

    setBuyingSeats(true);
    try {
      // Get current company data
      const { data: companyData, error: fetchError } = await supabaseData
        .from('companies')
        .select('purchased_seats')
        .eq('id', companyId)
        .single();

      if (fetchError) throw fetchError;

      const currentSeats = companyData?.purchased_seats || 0;
      const newTotal = currentSeats + seatsToBuy;

      const { error } = await supabaseData
        .from('companies')
        .update({ purchased_seats: newTotal })
        .eq('id', companyId);

      if (error) throw error;

      showSuccess(`Successfully added ${seatsToBuy} seat${seatsToBuy > 1 ? 's' : ''}! You now have ${newTotal} extra seat${newTotal > 1 ? 's' : ''}.`);
      setSeatsToBuy(1);
      refreshFeatures();
      loadTeamData();
    } catch (error) {
      showError('Failed to add seats: ' + error.message);
    } finally {
      setBuyingSeats(false);
    }
  };

  // Role definitions with descriptions
  const ROLES = {
    owner: {
      label: 'Owner',
      color: colors.accent,
      icon: <Crown size={16} color={colors.accent} />,
      description: 'Full access including billing and settings'
    },
    admin: {
      label: 'Admin',
      color: '#3498db',
      icon: <Shield size={16} color="#3498db" />,
      description: 'Full access except billing'
    },
    office: {
      label: 'Office',
      color: '#9b59b6',
      icon: <Users size={16} color="#9b59b6" />,
      description: 'Quotes, invoices, scheduling, customers'
    },
    field_tech: {
      label: 'Field Tech',
      color: '#4ecca3',
      icon: <SettingsIcon size={16} color="#4ecca3" />,
      description: 'Mobile view, assigned jobs only'
    },
    member: {
      label: 'Member',
      color: colors.textMuted,
      icon: <Users size={16} color={colors.textMuted} />,
      description: 'Basic access'
    }
  };

  const getRoleIcon = (role) => ROLES[role]?.icon || ROLES.member.icon;
  const getRoleColor = (role) => ROLES[role]?.color || ROLES.member.color;
  const getRoleLabel = (role) => ROLES[role]?.label || role;

  // Permissions configuration
  const PERMISSION_MODULES = [
    { id: 'home', label: 'Dashboard (Today)' },
    { id: 'field-view', label: 'My Jobs (Field View)' },
    { id: 'customers', label: 'Customers & Leads' },
    { id: 'work-orders', label: 'Work Orders' },
    { id: 'schedule', label: 'Schedule' },
    { id: 'products-pricing', label: 'Products & Pricing' },
    { id: 'vendors', label: 'Vendors' },
    { id: 'sales-tool', label: 'Sales Tool' },
    { id: 'financial', label: 'Reporting & Finance' },
    { id: 'money', label: 'Money' },
    { id: 'team', label: 'Team Management' },
    { id: 'referral', label: 'Earn With WorkVan' },
    { id: 'settings', label: 'Settings' }
  ];

  const DEFAULT_PERMISSIONS = {
    admin: ['home', 'customers', 'work-orders', 'schedule', 'products-pricing', 'vendors', 'sales-tool', 'financial', 'money', 'team', 'referral', 'settings'],
    office: ['home', 'customers', 'work-orders', 'schedule', 'products-pricing', 'vendors', 'sales-tool'],
    field_tech: ['field-view', 'work-orders', 'sales-tool'],
    member: ['home', 'customers', 'work-orders', 'schedule', 'sales-tool']
  };

  const EDITABLE_ROLES = ['admin', 'office', 'field_tech', 'member'];

  const loadPermissions = async () => {
    try {
      const { data, error } = await supabaseData
        .from('companies')
        .select('role_permissions')
        .eq('id', companyId)
        .single();
      if (!error && data?.role_permissions) {
        setRolePermissions(data.role_permissions);
      } else {
        setRolePermissions({ ...DEFAULT_PERMISSIONS });
      }
    } catch {
      setRolePermissions({ ...DEFAULT_PERMISSIONS });
    }
  };

  const togglePermission = (role, moduleId) => {
    setRolePermissions(prev => {
      const updated = { ...prev };
      const rolePerms = [...(updated[role] || [])];
      const idx = rolePerms.indexOf(moduleId);
      if (idx >= 0) {
        rolePerms.splice(idx, 1);
      } else {
        rolePerms.push(moduleId);
      }
      updated[role] = rolePerms;
      return updated;
    });
  };

  const savePermissions = async () => {
    setSavingPermissions(true);
    try {
      const { error } = await supabaseData
        .from('companies')
        .update({ role_permissions: rolePermissions })
        .eq('id', companyId);
      if (error) throw error;
      showSuccess('Permissions saved! Changes take effect on next login.');
    } catch (error) {
      showError('Failed to save permissions: ' + error.message);
    } finally {
      setSavingPermissions(false);
    }
  };

  const resetPermissions = () => {
    setRolePermissions({ ...DEFAULT_PERMISSIONS });
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: colors.textMuted }}>
        Loading team...
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ color: '#e74c3c', marginBottom: '1rem' }}>
          Failed to load team data
        </div>
        <div style={{ color: colors.textMuted, fontSize: '0.9rem', marginBottom: '1.5rem' }}>
          {loadError}
        </div>
        <button
          onClick={handleRetry}
          style={{
            padding: '0.75rem 1.5rem',
            background: colors.accent,
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <RefreshCw size={16} />
          Retry
        </button>
      </div>
    );
  }

  // Calculate seats: maxUsers already includes owner + purchased seats
  const maxUsers = getLimit('maxUsers'); // e.g., Team(3) + purchased(2) = 5
  const totalSeats = maxUsers; // Owner is 1 of these seats, not in addition
  const usedSeats = teamMembers.length + pendingInvites.length;
  const slotsRemaining = Math.max(0, totalSeats - usedSeats);
  const atUserLimit = usedSeats >= totalSeats;

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
      {/* Upgrade Banner when at limit */}
      {atUserLimit && isOwnerOrAdmin && (
        <div style={{
          background: colors.accentGradient,
          borderRadius: '12px',
          padding: '1.25rem 1.5rem',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <Zap size={24} color="#fff" />
            <div>
              <div style={{ color: '#fff', fontWeight: 'bold', fontSize: '1.1rem' }}>
                Team limit reached!
              </div>
              <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem' }}>
                Your {tierName} plan includes {maxUsers} team member{maxUsers === 1 ? '' : 's'}. Upgrade to add more.
              </div>
            </div>
          </div>
          {onNavigate && (
            <button
              onClick={() => onNavigate('settings')}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#fff',
                color: colors.accent,
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                minHeight: '44px'
              }}
            >
              Upgrade Plan
            </button>
          )}
        </div>
      )}

      {/* Header with Stats */}
      <div style={{
        background: colors.bgCard,
        borderRadius: '12px',
        padding: '2rem',
        marginBottom: '2rem',
        border: `1px solid ${colors.border}`
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '1.5rem',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div>
            <h2 style={{ fontSize: '2rem', margin: '0 0 0.5rem 0', color: colors.text }}>
              Team Management
            </h2>
            <p style={{ margin: 0, color: colors.textMuted }}>
              Manage your team members and invitations
              {maxUsers < 999 && (
                <span style={{ marginLeft: '0.5rem', color: atUserLimit ? colors.accent : '#4ecca3' }}>
                  ({usedSeats} of {totalSeats} seat{totalSeats === 1 ? '' : 's'} used)
                </span>
              )}
            </p>
          </div>

          {isOwnerOrAdmin && (
            <button
              onClick={() => setShowInviteModal(true)}
              disabled={atUserLimit}
              style={{
                padding: '0.75rem 1.5rem',
                background: atUserLimit ? colors.bgHover : colors.accent,
                color: atUserLimit ? colors.textMuted : '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: 'bold',
                cursor: atUserLimit ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                minHeight: '44px'
              }}
            >
              <UserPlus size={20} />
              {atUserLimit ? 'Upgrade to Add' : 'Invite Team Member'}
            </button>
          )}
        </div>

        {/* Team Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          paddingTop: '1.5rem',
          borderTop: `1px solid ${colors.border}`
        }}>
          <div>
            <div style={{ color: colors.textMuted, fontSize: '0.85rem', marginBottom: '0.25rem' }}>
              Team Members
            </div>
            <div style={{ color: colors.text, fontSize: '1.5rem', fontWeight: 'bold' }}>
              {teamMembers.length} of {totalSeats}
            </div>
          </div>
          <div>
            <div style={{ color: colors.textMuted, fontSize: '0.85rem', marginBottom: '0.25rem' }}>
              Pending Invitations
            </div>
            <div style={{ color: '#f39c12', fontSize: '1.5rem', fontWeight: 'bold' }}>
              {pendingInvites.length}
            </div>
          </div>
          <div>
            <div style={{ color: colors.textMuted, fontSize: '0.85rem', marginBottom: '0.25rem' }}>
              Seats Available
            </div>
            <div style={{
              color: slotsRemaining > 0 ? '#4ecca3' : '#e74c3c',
              fontSize: '1.5rem',
              fontWeight: 'bold'
            }}>
              {slotsRemaining}
            </div>
          </div>
        </div>

        {slotsRemaining <= 0 && (
          <div style={{
            marginTop: '1rem',
            padding: '1rem',
            background: '#2a1a1a',
            border: '1px solid #e74c3c',
            borderRadius: '8px',
            color: '#e74c3c',
            fontSize: '0.9rem'
          }}>
            ⚠️ No seats available. Upgrade your plan to add more team members.
          </div>
        )}
      </div>

      {/* Team Members */}
      <div style={{
        background: colors.bgCard,
        borderRadius: '12px',
        padding: '2rem',
        marginBottom: '2rem',
        border: `1px solid ${colors.border}`
      }}>
        <h3 style={{ margin: '0 0 1.5rem 0', color: colors.text }}>
          Active Members
        </h3>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {teamMembers.map(member => (
            <div
              key={member.id}
              style={{
                background: colors.bgInput,
                padding: '1.25rem',
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                border: `1px solid ${colors.border}`,
                flexWrap: 'wrap',
                gap: '1rem'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1, minWidth: '250px' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: colors.bgHover,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.25rem',
                  fontWeight: 'bold',
                  color: colors.accent,
                  flexShrink: 0
                }}>
                  {member.user_profiles?.full_name?.[0]?.toUpperCase() || 
                   member.user_profiles?.email?.[0]?.toUpperCase() || '?'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: colors.text, fontWeight: 'bold', marginBottom: '0.25rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {member.user_profiles?.full_name || member.user_profiles?.email}
                  </div>
                  <div style={{ color: colors.textMuted, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {member.user_profiles?.email}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                {/* Role badge/dropdown */}
                {isOwnerOrAdmin && member.role !== 'owner' ? (
                  <select
                    value={member.role}
                    onChange={(e) => handleChangeRole(member.id, e.target.value)}
                    style={{
                      padding: '0.5rem 0.75rem',
                      background: `${getRoleColor(member.role)}20`,
                      border: `1px solid ${getRoleColor(member.role)}50`,
                      borderRadius: '6px',
                      fontSize: '0.85rem',
                      fontWeight: 'bold',
                      color: getRoleColor(member.role),
                      cursor: 'pointer',
                      appearance: 'none',
                      paddingRight: '1.5rem',
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 0.5rem center'
                    }}
                  >
                    {Object.entries(ROLES)
                      .filter(([key]) => key !== 'owner')
                      .map(([key, role]) => (
                        <option key={key} value={key}>{role.label}</option>
                      ))
                    }
                  </select>
                ) : (
                  <div style={{
                    padding: '0.5rem 1rem',
                    background: `${getRoleColor(member.role)}20`,
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.85rem',
                    fontWeight: 'bold',
                    color: getRoleColor(member.role)
                  }}>
                    {getRoleIcon(member.role)}
                    {getRoleLabel(member.role)}
                  </div>
                )}

                {isOwnerOrAdmin && member.role !== 'owner' && (
                  <button
                    onClick={() => handleRemoveMember(member.id, member.user_profiles?.email)}
                    style={{
                      padding: '0.5rem',
                      background: colors.bgHover,
                      border: 'none',
                      borderRadius: '6px',
                      color: '#e74c3c',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                    title="Remove member"
                  >
                    <Trash2 size={18} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Add Seats Section */}
        {isOwnerOrAdmin && canPurchaseMoreSeats() && (
          <div style={{
            marginTop: '1.5rem',
            paddingTop: '1.5rem',
            borderTop: `1px solid ${colors.border}`
          }}>
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'stretch' : 'center',
              justifyContent: 'space-between',
              gap: '1rem'
            }}>
              <div>
                <h4 style={{ margin: '0 0 0.25rem 0', color: colors.text, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Users size={18} style={{ color: '#3498db' }} />
                  Need more team members?
                </h4>
                <p style={{ margin: 0, color: colors.textMuted, fontSize: '0.85rem' }}>
                  Add extra seats for ${SEAT_PRICE.toFixed(2)}/month each
                  {tier === 'pro' && ` (${getRemainingPurchasableSeats()} available)`}
                </p>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                flexWrap: 'wrap',
                justifyContent: isMobile ? 'space-between' : 'flex-end'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <button
                    onClick={() => setSeatsToBuy(Math.max(1, seatsToBuy - 1))}
                    style={{
                      width: '36px',
                      height: '36px',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '6px',
                      color: colors.text,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Minus size={14} />
                  </button>
                  <span style={{
                    width: '40px',
                    textAlign: 'center',
                    color: colors.text,
                    fontSize: '1.1rem',
                    fontWeight: 'bold'
                  }}>
                    {seatsToBuy}
                  </span>
                  <button
                    onClick={() => {
                      const remaining = getRemainingPurchasableSeats();
                      setSeatsToBuy(Math.min(seatsToBuy + 1, remaining));
                    }}
                    disabled={seatsToBuy >= getRemainingPurchasableSeats()}
                    style={{
                      width: '36px',
                      height: '36px',
                      background: seatsToBuy >= getRemainingPurchasableSeats() ? colors.bgCard : colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '6px',
                      color: seatsToBuy >= getRemainingPurchasableSeats() ? colors.textMuted : colors.text,
                      cursor: seatsToBuy >= getRemainingPurchasableSeats() ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Plus size={14} />
                  </button>
                </div>

                <span style={{ color: colors.textMuted, fontSize: '0.9rem' }}>
                  = ${(seatsToBuy * SEAT_PRICE).toFixed(2)}/mo
                </span>

                <button
                  onClick={handleBuySeats}
                  disabled={buyingSeats || seatsToBuy > getRemainingPurchasableSeats()}
                  style={{
                    padding: '0.6rem 1.25rem',
                    background: buyingSeats ? colors.bgHover : '#4ecca3',
                    color: buyingSeats ? colors.textMuted : '#000',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    cursor: buyingSeats ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    minHeight: '44px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <Plus size={16} />
                  {buyingSeats ? 'Adding...' : 'Add Seats'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Upgrade prompt when can't purchase more seats on Pro */}
        {isOwnerOrAdmin && !canPurchaseMoreSeats() && tier === 'pro' && (
          <div style={{
            marginTop: '1.5rem',
            paddingTop: '1.5rem',
            borderTop: `1px solid ${colors.border}`
          }}>
            <div style={{
              background: '#1a2a3a',
              borderRadius: '8px',
              padding: '1rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '1rem',
              flexWrap: 'wrap',
              border: '1px solid #3498db40'
            }}>
              <div>
                <div style={{ color: colors.text, fontWeight: 'bold' }}>
                  Pro Plan Seat Limit Reached
                </div>
                <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                  Upgrade to Team for unlimited additional seats
                </div>
              </div>
              {onNavigate && (
                <button
                  onClick={() => onNavigate('settings')}
                  style={{
                    padding: '0.6rem 1.25rem',
                    background: '#3498db',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    minHeight: '44px'
                  }}
                >
                  Upgrade Plan
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Flex Workers Section - Growth+ only */}
      {hasFlexWorkers() && (
        <div style={{
          background: colors.bgCard,
          borderRadius: '12px',
          padding: '2rem',
          marginBottom: '2rem',
          border: '1px solid #e74c3c40'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem',
            flexWrap: 'wrap',
            gap: '1rem'
          }}>
            <div>
              <h3 style={{ margin: '0 0 0.25rem 0', color: colors.text, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Clock size={20} color="#e74c3c" />
                Flex Workers (1099 Contractors)
              </h3>
              <p style={{ margin: 0, color: colors.textMuted, fontSize: '0.9rem' }}>
                Temporary workers you can activate for specific days
              </p>
            </div>
            <button
              onClick={() => setShowFlexModal(true)}
              style={{
                padding: '0.75rem 1.25rem',
                background: '#e74c3c',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.9rem'
              }}
            >
              <Plus size={18} />
              Add Flex Worker
            </button>
          </div>

          {/* Flex Days Usage */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '1rem',
            marginBottom: '1.5rem',
            padding: '1rem',
            background: colors.bgInput,
            borderRadius: '8px'
          }}>
            <div>
              <div style={{ color: colors.textMuted, fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                Flex Days Used
              </div>
              <div style={{ color: '#e74c3c', fontSize: '1.5rem', fontWeight: 'bold' }}>
                {flexDaysUsed} / {flexDaysIncluded}
              </div>
            </div>
            <div>
              <div style={{ color: colors.textMuted, fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                Days Remaining
              </div>
              <div style={{
                color: flexDaysRemaining > 5 ? '#4ecca3' : flexDaysRemaining > 0 ? '#f39c12' : '#e74c3c',
                fontSize: '1.5rem',
                fontWeight: 'bold'
              }}>
                {flexDaysRemaining}
              </div>
            </div>
            <div>
              <div style={{ color: colors.textMuted, fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                Resets On
              </div>
              <div style={{ color: colors.textMuted, fontSize: '1.25rem', fontWeight: 'bold' }}>
                {new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </div>
            </div>
          </div>

          {/* Flex Workers List */}
          {flexWorkers.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '2rem',
              color: colors.textMuted
            }}>
              <UserCheck size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
              <p>No flex workers added yet.</p>
              <p style={{ fontSize: '0.85rem' }}>Add 1099 contractors you work with occasionally.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {flexWorkers.map(worker => (
                <div
                  key={worker.id}
                  style={{
                    background: colors.bgInput,
                    padding: '1rem',
                    borderRadius: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    border: `1px solid ${colors.border}`,
                    flexWrap: 'wrap',
                    gap: '0.75rem'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: '#e74c3c20',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#e74c3c',
                      fontWeight: 'bold'
                    }}>
                      {worker.name?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div>
                      <div style={{ color: colors.text, fontWeight: '600' }}>{worker.name}</div>
                      <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                        {worker.phone && formatPhoneNumber(worker.phone)}
                        {worker.phone && worker.email && ' • '}
                        {worker.email}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={() => activateFlexWorker(worker)}
                      disabled={flexDaysRemaining <= 0}
                      style={{
                        padding: '0.5rem 1rem',
                        background: flexDaysRemaining > 0 ? '#4ecca3' : colors.bgHover,
                        color: flexDaysRemaining > 0 ? '#000' : colors.textMuted,
                        border: 'none',
                        borderRadius: '6px',
                        fontWeight: '600',
                        cursor: flexDaysRemaining > 0 ? 'pointer' : 'not-allowed',
                        fontSize: '0.85rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      <UserCheck size={16} />
                      Activate Today
                    </button>
                    <button
                      onClick={() => removeFlexWorker(worker)}
                      style={{
                        padding: '0.5rem',
                        background: colors.bgHover,
                        color: '#e74c3c',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Flex Worker Modal */}
      {showFlexModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: colors.bgCard,
            borderRadius: '12px',
            padding: '2rem',
            width: '100%',
            maxWidth: '450px',
            border: `1px solid ${colors.border}`
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, color: colors.text }}>Add Flex Worker</h3>
              <button
                onClick={() => setShowFlexModal(false)}
                style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer' }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>
                  Name *
                </label>
                <input
                  type="text"
                  value={flexForm.name}
                  onChange={(e) => setFlexForm({ ...flexForm, name: e.target.value })}
                  placeholder="John Smith"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1rem'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>
                  Phone
                </label>
                <input
                  type="tel"
                  value={flexForm.phone}
                  onChange={(e) => setFlexForm({ ...flexForm, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1rem'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: '600' }}>
                  Email
                </label>
                <input
                  type="email"
                  value={flexForm.email}
                  onChange={(e) => setFlexForm({ ...flexForm, email: e.target.value })}
                  placeholder="john@example.com"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.text,
                    fontSize: '1rem'
                  }}
                />
              </div>

              <button
                onClick={addFlexWorker}
                disabled={savingFlex || !flexForm.name.trim()}
                style={{
                  marginTop: '0.5rem',
                  padding: '0.875rem',
                  background: savingFlex || !flexForm.name.trim() ? colors.bgHover : '#e74c3c',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  cursor: savingFlex || !flexForm.name.trim() ? 'not-allowed' : 'pointer',
                  fontSize: '1rem'
                }}
              >
                {savingFlex ? 'Adding...' : 'Add Flex Worker'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pending Invitations */}
      {pendingInvites.length > 0 && (
        <div style={{
          background: colors.bgCard,
          borderRadius: '12px',
          padding: '2rem',
          border: `1px solid ${colors.border}`
        }}>
          <h3 style={{ margin: '0 0 1.5rem 0', color: colors.text }}>
            Pending Invitations ({pendingInvites.length})
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {pendingInvites.map(invite => (
              <div
                key={invite.id}
                style={{
                  background: colors.bgInput,
                  padding: '1.25rem',
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  border: '1px solid #f39c1240',
                  flexWrap: 'wrap',
                  gap: '1rem'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1, minWidth: '250px' }}>
                  {invite.invite_method === 'sms' ? (
                    <MessageSquare size={32} color="#f39c12" style={{ flexShrink: 0 }} />
                  ) : invite.invite_method === 'both' ? (
                    <div style={{ display: 'flex', flexShrink: 0 }}>
                      <Mail size={24} color="#f39c12" />
                      <MessageSquare size={24} color="#f39c12" style={{ marginLeft: '-8px' }} />
                    </div>
                  ) : (
                    <Mail size={32} color="#f39c12" style={{ flexShrink: 0 }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: colors.text, fontWeight: 'bold', marginBottom: '0.25rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {invite.email || (invite.phone ? formatPhoneNumber(invite.phone) : 'No contact')}
                      {invite.email && invite.phone && (
                        <span style={{ color: colors.textMuted, fontWeight: 'normal', marginLeft: '0.5rem', fontSize: '0.85rem' }}>
                          & {formatPhoneNumber(invite.phone)}
                        </span>
                      )}
                    </div>
                    <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                      Invited {new Date(invite.created_at).toLocaleDateString()} •
                      Expires {new Date(invite.expires_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{
                    padding: '0.5rem 1rem',
                    background: `${getRoleColor(invite.role)}20`,
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    fontWeight: 'bold',
                    color: getRoleColor(invite.role),
                    textTransform: 'capitalize'
                  }}>
                    {(invite.role || 'member').replace('_', ' ')}
                  </div>

                  {isOwnerOrAdmin && (
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={() => openResendModal(invite)}
                        style={{
                          padding: '0.5rem 0.75rem',
                          background: colors.bgHover,
                          border: 'none',
                          borderRadius: '6px',
                          color: '#4ecca3',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.35rem',
                          fontSize: '0.85rem'
                        }}
                        title="Resend invitation"
                      >
                        <RefreshCw size={14} />
                        Resend
                      </button>
                      <button
                        onClick={() => handleCancelInvite(invite.id)}
                        style={{
                          padding: '0.5rem',
                          background: colors.bgHover,
                          border: 'none',
                          borderRadius: '6px',
                          color: '#e74c3c',
                          cursor: 'pointer'
                        }}
                        title="Cancel invitation"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Role Permissions Section */}
      {isOwnerOrAdmin && (
        <div style={{
          background: colors.bgCard,
          borderRadius: '12px',
          padding: '2rem',
          marginBottom: '2rem',
          border: `1px solid ${colors.border}`
        }}>
          <div
            onClick={() => {
              if (!showPermissions) loadPermissions();
              setShowPermissions(!showPermissions);
            }}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'pointer'
            }}
          >
            <h3 style={{ margin: 0, color: colors.text, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Lock size={20} color={colors.accent} />
              Role Permissions
            </h3>
            <span style={{ color: colors.textMuted, fontSize: '0.9rem' }}>
              {showPermissions ? 'Hide' : 'Customize access for each role'}
            </span>
          </div>

          {showPermissions && rolePermissions && (
            <div style={{ marginTop: '1.5rem' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: isMobile ? '0.8rem' : '0.9rem'
                }}>
                  <thead>
                    <tr>
                      <th style={{
                        textAlign: 'left',
                        padding: '0.75rem 0.5rem',
                        borderBottom: `1px solid ${colors.border}`,
                        color: colors.textMuted,
                        fontWeight: 'normal',
                        minWidth: isMobile ? '120px' : '180px',
                        position: 'sticky',
                        left: 0,
                        background: colors.bgCard,
                        zIndex: 1
                      }}>
                        Module
                      </th>
                      {EDITABLE_ROLES.map(role => (
                        <th key={role} style={{
                          textAlign: 'center',
                          padding: '0.75rem 0.5rem',
                          borderBottom: `1px solid ${colors.border}`,
                          color: getRoleColor(role),
                          fontWeight: 'bold',
                          minWidth: isMobile ? '60px' : '80px'
                        }}>
                          {isMobile ? getRoleLabel(role).slice(0, 5) : getRoleLabel(role)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {PERMISSION_MODULES.map(mod => (
                      <tr key={mod.id}>
                        <td style={{
                          padding: '0.6rem 0.5rem',
                          borderBottom: `1px solid ${colors.borderLight}`,
                          color: colors.text,
                          position: 'sticky',
                          left: 0,
                          background: colors.bgCard,
                          zIndex: 1
                        }}>
                          {mod.label}
                        </td>
                        {EDITABLE_ROLES.map(role => {
                          const hasAccess = (rolePermissions[role] || []).includes(mod.id);
                          return (
                            <td key={role} style={{
                              textAlign: 'center',
                              padding: '0.6rem 0.5rem',
                              borderBottom: `1px solid ${colors.borderLight}`
                            }}>
                              <button
                                onClick={() => togglePermission(role, mod.id)}
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  borderRadius: '6px',
                                  border: hasAccess ? `2px solid ${getRoleColor(role)}` : `2px solid ${colors.border}`,
                                  background: hasAccess ? `${getRoleColor(role)}20` : 'transparent',
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.15s'
                                }}
                              >
                                {hasAccess && <Check size={16} color={getRoleColor(role)} />}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{
                marginTop: '1.5rem',
                display: 'flex',
                gap: '0.75rem',
                justifyContent: 'flex-end',
                flexWrap: 'wrap'
              }}>
                <button
                  onClick={resetPermissions}
                  style={{
                    padding: '0.6rem 1.25rem',
                    background: colors.bgInput,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    color: colors.textMuted,
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  Reset to Defaults
                </button>
                <button
                  onClick={savePermissions}
                  disabled={savingPermissions}
                  style={{
                    padding: '0.6rem 1.25rem',
                    background: savingPermissions ? colors.bgHover : colors.accent,
                    color: savingPermissions ? colors.textMuted : '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: savingPermissions ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: 'bold'
                  }}
                >
                  {savingPermissions ? 'Saving...' : 'Save Permissions'}
                </button>
              </div>

              <p style={{ color: colors.textMuted, fontSize: '0.8rem', marginTop: '0.75rem', marginBottom: 0 }}>
                Owner always has full access. Changes apply on the team member's next login.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem',
          overflow: 'auto'
        }}>
          <div style={{
            background: colors.bgCard,
            borderRadius: '12px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            border: `1px solid ${colors.border}`,
            margin: 'auto'
          }}>
            <div style={{
              padding: '1.5rem',
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, color: colors.text, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <UserPlus size={20} color={colors.accent} />
                Invite Team Member
              </h3>
              <button
                onClick={() => setShowInviteModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: colors.textMuted,
                  cursor: 'pointer',
                  padding: '0.5rem'
                }}
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSendInvite} style={{ padding: '2rem' }}>
              {/* Invite Method Selection */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: colors.text,
                  fontWeight: 'bold'
                }}>
                  Send Invite Via
                </label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {[
                    { id: 'email', label: 'Email', icon: <Mail size={16} /> },
                    { id: 'sms', label: 'Text', icon: <MessageSquare size={16} /> },
                    { id: 'both', label: 'Both', icon: <><Mail size={14} /><MessageSquare size={14} /></> }
                  ].map(method => (
                    <button
                      key={method.id}
                      type="button"
                      onClick={() => setInviteMethod(method.id)}
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        background: inviteMethod === method.id ? '#4ecca320' : colors.bgInput,
                        border: inviteMethod === method.id ? '2px solid #4ecca3' : `2px solid ${colors.border}`,
                        borderRadius: '8px',
                        color: inviteMethod === method.id ? '#4ecca3' : colors.textMuted,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        fontWeight: inviteMethod === method.id ? 'bold' : 'normal',
                        fontSize: '0.9rem'
                      }}
                    >
                      {method.icon}
                      {method.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Email Field */}
              {(inviteMethod === 'email' || inviteMethod === 'both') && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: colors.text,
                    fontWeight: 'bold'
                  }}>
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="teammate@example.com"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '1rem'
                    }}
                  />
                </div>
              )}

              {/* Phone Field */}
              {(inviteMethod === 'sms' || inviteMethod === 'both') && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: colors.text,
                    fontWeight: 'bold'
                  }}>
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    value={invitePhone}
                    onChange={(e) => {
                      // Format as user types
                      const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                      if (digits.length >= 6) {
                        setInvitePhone(`(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`);
                      } else if (digits.length >= 3) {
                        setInvitePhone(`(${digits.slice(0, 3)}) ${digits.slice(3)}`);
                      } else {
                        setInvitePhone(digits);
                      }
                    }}
                    placeholder="(555) 123-4567"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '1rem'
                    }}
                  />
                </div>
              )}

              <div style={{ marginBottom: '2rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: colors.text,
                  fontWeight: 'bold'
                }}>
                  Role *
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {Object.entries(ROLES)
                    .filter(([key]) => key !== 'owner' || userRole === 'owner')
                    .map(([key, role]) => (
                      <label
                        key={key}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '0.75rem',
                          padding: '0.75rem 1rem',
                          background: inviteRole === key ? `${role.color}20` : colors.bgInput,
                          border: inviteRole === key ? `2px solid ${role.color}` : '2px solid transparent',
                          borderRadius: '8px',
                          cursor: 'pointer'
                        }}
                      >
                        <input
                          type="radio"
                          name="role"
                          value={key}
                          checked={inviteRole === key}
                          onChange={(e) => setInviteRole(e.target.value)}
                          style={{ marginTop: '0.25rem' }}
                        />
                        <div>
                          <div style={{
                            color: role.color,
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}>
                            {role.icon}
                            {role.label}
                          </div>
                          <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                            {role.description}
                          </div>
                        </div>
                      </label>
                    ))
                  }
                </div>
              </div>

              <button
                type="submit"
                disabled={sending}
                style={{
                  width: '100%',
                  padding: '1rem',
                  background: sending ? colors.bgHover : colors.accent,
                  color: sending ? colors.textMuted : '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  cursor: sending ? 'not-allowed' : 'pointer'
                }}
              >
                {sending ? 'Sending Invitation...' : 'Send Invitation'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Resend Invite Modal */}
      {showResendModal && resendInvite && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: colors.bgCard,
            borderRadius: '12px',
            maxWidth: '450px',
            width: '100%',
            border: `1px solid ${colors.border}`
          }}>
            <div style={{
              padding: '1.5rem',
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, color: colors.text, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <RefreshCw size={20} color="#4ecca3" />
                Resend Invitation
              </h3>
              <button
                onClick={() => { setShowResendModal(false); setResendInvite(null); }}
                style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer', padding: '0.5rem' }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              {/* Send method */}
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
                  Send Via
                </label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {[
                    { id: 'email', label: 'Email', icon: <Mail size={16} /> },
                    { id: 'sms', label: 'Text', icon: <MessageSquare size={16} /> },
                    { id: 'both', label: 'Both', icon: <><Mail size={14} /><MessageSquare size={14} /></> }
                  ].map(method => (
                    <button
                      key={method.id}
                      type="button"
                      onClick={() => setResendMethod(method.id)}
                      style={{
                        flex: 1,
                        padding: '0.6rem',
                        background: resendMethod === method.id ? '#4ecca320' : colors.bgInput,
                        border: resendMethod === method.id ? '2px solid #4ecca3' : `2px solid ${colors.border}`,
                        borderRadius: '8px',
                        color: resendMethod === method.id ? '#4ecca3' : colors.textMuted,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        fontWeight: resendMethod === method.id ? 'bold' : 'normal',
                        fontSize: '0.9rem'
                      }}
                    >
                      {method.icon}
                      {method.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Email */}
              {(resendMethod === 'email' || resendMethod === 'both') && (
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    placeholder="teammate@example.com"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '1rem'
                    }}
                  />
                </div>
              )}

              {/* Phone */}
              {(resendMethod === 'sms' || resendMethod === 'both') && (
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: colors.text, fontWeight: 'bold' }}>
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={resendPhone}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                      if (digits.length >= 6) {
                        setResendPhone(`(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`);
                      } else if (digits.length >= 3) {
                        setResendPhone(`(${digits.slice(0, 3)}) ${digits.slice(3)}`);
                      } else {
                        setResendPhone(digits);
                      }
                    }}
                    placeholder="(555) 123-4567"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: colors.bgInput,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '8px',
                      color: colors.text,
                      fontSize: '1rem'
                    }}
                  />
                </div>
              )}

              {/* Role display */}
              <div style={{
                padding: '0.75rem',
                background: colors.bgInput,
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: colors.textMuted,
                fontSize: '0.9rem'
              }}>
                Role: <span style={{ color: colors.text, fontWeight: 'bold', textTransform: 'capitalize' }}>
                  {(resendInvite.role || 'member').replace('_', ' ')}
                </span>
              </div>

              <button
                onClick={handleResendInvite}
                disabled={resending}
                style={{
                  width: '100%',
                  padding: '0.875rem',
                  background: resending ? colors.bgHover : '#4ecca3',
                  color: resending ? colors.textMuted : '#000',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  cursor: resending ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                <RefreshCw size={18} />
                {resending ? 'Sending...' : 'Resend Invitation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
